import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    cancelMaintenanceMonthlyCost,
    deleteMaintenanceMonthlyCost,
    getMaintenanceMonthlyCostList,
} from '../../services/MaintenanceServices';

function MaintenanceMonthlyCostTable() {
    const [data, setData] = useState({ records: [] });
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [plantFilter, setPlantFilter] = useState('');
    const now = new Date();

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            const list = (response.plant_list || [])
                .filter((p) => p.status === true || p.status === 1 || p.status === 'Active')
                .map((p) => ({ value: p.id, label: p.plant_alias }));
            setPlants(list);
            if (list.length === 1) setPlantFilter(String(list[0].value));
        });
    }, [cookies]);

    const fetchRecords = useCallback(() => {
        if (!plantFilter) {
            setIsLoading(false);
            return Promise.resolve();
        }
        setIsLoading(true);
        return getMaintenanceMonthlyCostList(cookies, {
            plant_id: plantFilter,
            cost_year: now.getFullYear(),
        })
            .then((response) => {
                setData({
                    records: (response.maintenance_monthly_cost_list || []).map((row) => {
                        const isActive = row.status === true || row.status === 1 || row.status === 'Active';
                        return {
                            ...row,
                            period: `${row.cost_month}/${row.cost_year}`,
                            status_label: isActive ? 'Active' : 'Cancelled',
                            hide_row_edit: isActive ? 'No' : 'Yes',
                            hide_row_delete: 'No',
                        };
                    }),
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load month-end costs.' }, 'Load Failed');
                setData({ records: [] });
            })
            .finally(() => setIsLoading(false));
    }, [cookies, plantFilter]);

    useEffect(() => {
        fetchRecords();
    }, [fetchRecords]);

    const columns = useMemo(() => ([
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'period', headerName: 'Period' },
        { fieldName: 'cost_type', headerName: 'Cost Type' },
        { fieldName: 'bill_amount', headerName: 'Bill Amount', align: 'right' },
        { fieldName: 'bill_kwh', headerName: 'Bill kWh', align: 'right' },
        { fieldName: 'allocation_method', headerName: 'Allocation' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ]), []);

    const handleEdit = (id) => navigate(`/MaintenanceMonthlyCostEdit/${id}`);

    const handleDelete = (id) => {
        const row = data.records.find((r) => String(r.id) === String(id));
        const isActive = row?.status_label === 'Active';
        Swal.fire({
            title: isActive ? 'Cancel this month-end cost?' : 'Delete this month-end cost?',
            icon: 'warning',
            showCancelButton: true,
        }).then((result) => {
            if (!result.isConfirmed) return;
            const action = isActive
                ? cancelMaintenanceMonthlyCost(cookies, id)
                : deleteMaintenanceMonthlyCost(cookies, id);
            action
                .then(() => fetchRecords())
                .catch((error) => displayError(error?.response?.data || error, isActive ? 'Cancel Failed' : 'Delete Failed'));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="MAINTENANCE"
                    title="Month-End Costs"
                    subtitle="EB bills, pump costs and other monthly allocations"
                    onAdd={() => navigate('/MaintenanceMonthlyCost')}
                    onRefresh={fetchRecords}
                    onExportCSV={() => exportAsExcel(columns, data.records, 'maintenance_monthly_cost')}
                    isLoading={isLoading}
                >
                    <div className="mb-3 px-3">
                        <label className="mr-2">Plant</label>
                        <select
                            className="form-control d-inline-block w-auto"
                            value={plantFilter}
                            onChange={(e) => setPlantFilter(e.target.value)}
                        >
                            <option value="">Select Plant</option>
                            {plants.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
                        </select>
                    </div>
                    <ReactTableWrapper
                        data={data.records}
                        columns={columns}
                        isLoading={isLoading}
                        onRowEdit={handleEdit}
                        onRowDelete={handleDelete}
                    />
                </ListPageLayout>
            </div>
        </div>
    );
}

export default MaintenanceMonthlyCostTable;
